import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';

export const downloadButtonStyle = StyleSheet.create({
  downloadButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    zIndex: 3,
  },
});

const DownloadButton = React.memo(({ isHovered, onPress, isLoading, onHoverChange, label }) => {
  const [isFocused, setIsFocused] = useState(false);
  const { isXS } = useResponsiveness();
  const [canHover, setCanHover] = useState(() => (
    Platform.OS !== 'web' || typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(hover: hover) and (pointer: fine)').matches
  ));
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setCanHover(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);
  // Touch devices cannot reveal a hover-only action. Keep it available at
  // phone widths and on larger touch-only tablets as well as native apps.
  const visible = isHovered || isFocused || isXS || !canHover || Platform.OS !== 'web';
  return (
    <Pressable
      style={[
        downloadButtonStyle.downloadButton,
        visible && { opacity: 1 },
      ]}
      // Hidden download controls still sit on top of the media. Ignore them
      // until the card is hovered or the button is focused.
      pointerEvents={visible ? 'auto' : 'none'}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onPress={onPress}
      disabled={isLoading}
      accessibilityRole="button"
      accessibilityLabel={label}
      // react-native-web's contained hover ends the parent card's hover the
      // moment the cursor enters this nested Pressable; report our own hover
      // so the card can keep the button visible while it is being approached.
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      onHoverIn={() => onHoverChange?.(true)}
      onHoverOut={() => onHoverChange?.(false)}
    >
      {isLoading ? (
        <ActivityIndicator size="small" color="#fff" />
      ) : (
        <Icon name="arrow-down-to-line" size={18} color="#fff" />
      )}
    </Pressable>
  );
});

DownloadButton.propTypes = {
  isHovered: PropTypes.bool.isRequired,
  onPress: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onHoverChange: PropTypes.func,
  label: PropTypes.string.isRequired,
};

export default DownloadButton;
